import { useId, type ReactNode } from "react";
import type { OtpChannel } from "../types/api";
import { RadioGroup } from "./RadioGroup";

interface OtpChannelPickerProps {
  /** The selected channel. Callers seed their state to `"sms"`. */
  value: OtpChannel;
  onChange: (channel: OtpChannel) => void;
  /**
   * Group prompt; screens that know the target phone interpolate it (e.g.
   * "How should we send your code to ***1234?").
   */
  legend?: string | undefined;
  disabled?: boolean | undefined;
}

/**
 * Text-message or voice-call delivery choice for the OTP screens.
 *
 * It is a controlled wrapper over `RadioGroup`, so every code screen (member
 * portal login, partner login 2FA, partner signup phone-verify, org
 * phone-verify) presents the same choice. The picker stays visible after a
 * send, and "Resend code" uses whatever is selected at press time.
 */
export function OtpChannelPicker({
  value,
  onChange,
  legend = "How should we send your code?",
  disabled,
}: OtpChannelPickerProps): ReactNode {
  // Radios need a shared group name for native keyboard semantics; useId keeps
  // it unique per mounted picker (the org tab renders other radio groups).
  const name = useId();
  return (
    <RadioGroup
      legend={legend}
      name={name}
      options={[
        { value: "sms", label: "Text message" },
        { value: "call", label: "Voice call" },
      ]}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value === "call" ? "call" : "sms")}
    />
  );
}
